import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Input,
  Typography,
  message,
} from '@surecompanies/core_components'
import type {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridReadyEvent,
  IServerSideGetRowsParams,
  ModelUpdatedEvent,
} from '@ag-grid-community/core'
import {
  CheckOutlined,
  DownloadOutlined,
  ToolOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import camelCase from 'lodash/camelCase'
import mapKeys from 'lodash/mapKeys'
import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'hooks/useSearchParams'
import {
  getAuthHeader,
  getCompare,
  getDataTypeCsvExportUrl,
  getDeletedData,
  type CompareStatus,
  type DeletedDataType,
  type NewCompareDataType,
} from '../../../../api/plan-management-tasks-api'
import { getCompareReportColDefs } from '../../utils/compareGridColumns'
import {
  StageBadgeRenderer,
  STAGE_STYLES,
} from '../../utils/StageBadgeRenderer'
import {
  FIELD_CONFIG,
  IDENTITY_FIELDS,
  coerceParamValue,
} from '../../utils/planBenefitFields'
import {
  createOverrideRule,
  listOverrideRules,
  updateOverrideRule,
  updateOverrideRuleStatus,
} from '../../../PlanOverrides/api/plan-benefit-override-rules-api'
import {
  PlanBenefitOverrideRuleStatus,
  PlanBenefitOverrideRuleTemplateKey,
  PlanBenefitOverrideValueSource,
} from '../../../PlanOverrides/pages/PlanBenefitOverrides/enums/plan-benefit-override-rule.enum'
import { OVERRIDE_RULES_QUERY_KEY } from '../../../PlanOverrides/pages/PlanBenefitOverrides/pages/ListPlanBenefitOverride/hooks/useGetOverrideRules'
import { FIND_OVERRIDE_RULE_QUERY_KEY } from '../../../PlanOverrides/pages/PlanBenefitOverrides/hooks/useFindOverrideRuleByMatch'
import { IgnoreActionCell } from '../../components/IgnoreActionCell'
import PlanReviewGrid from '../../components/PlanReviewGrid'
import PlanSnapshotInfo from '../../components/PlanSnapshotInfo'
import ImportNewBenefitsModal from '../../components/ImportNewBenefitsModal'
import { RowActionCell } from '../../components/ApproveActionCell'
import PlanBenefitReviewModal, {
  type ReviewCellInfo,
  type ReviewSavedUpdate,
} from '../../components/PlanBenefitReviewModal'
import {
  PlanBenefitCellPopover,
  type PopoverCellInfo,
} from '../../components/PlanBenefitCellPopover'
import PlanAndRateReviewModal, {
  type PlanAndRateReviewCellInfo,
} from '../../components/PlanAndRateReviewModal'
import {
  PlanAndRateCellPopover,
  type PlanAndRatePopoverCellInfo,
} from '../../components/PlanAndRateCellPopover'
import {
  PLANS_AND_RATES_NON_EDITABLE_COLS,
  FIELD_CONFIG as PAR_FIELD_CONFIG,
  IDENTITY_FIELDS as PAR_IDENTITY_FIELDS,
  AREA_FACTOR_KEYS as PAR_AREA_FACTOR_KEYS,
  coerceParamValue as parCoerceParamValue,
  flattenRuleParams,
  nestRuleParams,
  getStagingValue,
} from '../../utils/planAndRateFields'
import {
  createPlanAndRateOverrideRule,
  findPlanAndRateOverrideRuleByMatch,
  updatePlanAndRateOverrideRule,
  updatePlanAndRateOverrideRuleStatus,
} from '../../../PlanOverrides/api/plan-and-rate-override-rules-api'
import {
  PlanAndRateOverrideRuleStatus,
  PlanAndRateOverrideValueSource,
} from '../../../PlanOverrides/pages/PlanAndRateOverrides/enums/plan-and-rate-override-rule.enum'
import { FIND_PLAN_AND_RATE_RULE_QUERY_KEY } from '../../../PlanOverrides/pages/PlanAndRateOverrides/hooks/useFindPlanAndRateRuleByMatch'
import styles from '../../ReviewPlanSnapshot.module.scss'
import dayjs from 'dayjs'

const PLAN_BENEFITS_IDENTITY = new Set([
  'year',
  'hiosPlanId',
  'state',
  'benefit',
  '__compareStatus',
])

const DEFAULT_PAGE_SIZE = 20
const VALID_PAGE_SIZES = new Set([10, 20, 50, 100, 500, 1000])

const ALL_STATUSES: CompareStatus[] = ['NEW', 'MODIFIED', 'UNCHANGED']

const STAGE_OPTIONS = [
  { label: 'New', value: 'NEW' },
  { label: 'Modified', value: 'MODIFIED' },
  { label: 'Unchanged', value: 'UNCHANGED' },
]

const STAGE_BADGE_COL_DEF = {
  field: '__compareStatus',
  headerName: '',
  pinned: 'left' as const,
  width: 110,
  sortable: false,
  filter: false,
  resizable: false,
  cellRenderer: StageBadgeRenderer,
  cellStyle: { display: 'flex', alignItems: 'center', padding: 0 },
}

function normalizeMigrateKeys(
  obj: Record<string, unknown>
): Record<string, unknown> {
  return mapKeys(obj, (_, key) => camelCase(key))
}

function checkDraftRows(api: GridApi): boolean {
  let found = false
  api.forEachNode((node) => {
    if (!found && node.data?.__overrideStatus === 'DRAFT') {
      found = true
    }
  })
  return found
}

interface DataTypeTabProps {
  taskId: string
  dataType: string
  enabled: boolean
  globalState?: string
  globalIssuerId?: string
  globalHiosPlanId?: string
}

export default function DataTypeTab({
  taskId,
  dataType,
  enabled,
  globalState,
  globalIssuerId,
  globalHiosPlanId,
}: DataTypeTabProps) {
  const { params, setParams } = useSearchParams()
  const queryClient = useQueryClient()

  const globalStates: string[] = globalState ? [globalState] : []

  const parsedInitialStatuses: CompareStatus[] = (() => {
    const raw = params.statuses as string | undefined
    if (!raw) return ALL_STATUSES
    const parsed = raw
      .split(',')
      .filter((s): s is CompareStatus => (ALL_STATUSES as string[]).includes(s))
    return parsed.length ? parsed : ALL_STATUSES
  })()
  const parsedInitialPageSize = (() => {
    const n = parseInt((params.pageSize as string) ?? '', 10)
    return VALID_PAGE_SIZES.has(n) ? n : DEFAULT_PAGE_SIZE
  })()
  const parsedInitialPage = (() => {
    const n = parseInt((params.page as string) ?? '1', 10)
    return n >= 1 ? n : 1
  })()
  const parsedInitialLocationSearch = (params.locationSearch as string) ?? ''

  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [selectedStatuses, setSelectedStatuses] = useState<CompareStatus[]>(
    parsedInitialStatuses
  )
  const [locationSearch, setLocationSearch] = useState(
    parsedInitialLocationSearch
  )

  const gridRef = useRef<GridReadyEvent | undefined>(undefined)
  const taskIdRef = useRef(taskId)
  taskIdRef.current = taskId
  const dataTypeRef = useRef(dataType)
  dataTypeRef.current = dataType
  const selectedStatusesRef = useRef<CompareStatus[]>(parsedInitialStatuses)
  const globalStatesRef = useRef<string[]>(globalStates)
  globalStatesRef.current = globalStates
  const globalIssuerIdRef = useRef(globalIssuerId)
  globalIssuerIdRef.current = globalIssuerId
  const globalHiosPlanIdRef = useRef(globalHiosPlanId)
  globalHiosPlanIdRef.current = globalHiosPlanId
  const locationSearchRef = useRef(parsedInitialLocationSearch)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initialPageRef = useRef(parsedInitialPage)
  const requestVersionRef = useRef(0)
  const totalRef = useRef(0)
  // Row that was just edited via the review modal — flashed once the grid
  // reloads so the user can spot which row they changed.
  const pendingHighlightRef = useRef<string | null>(null)
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    selectedStatusesRef.current = parsedInitialStatuses
    locationSearchRef.current = parsedInitialLocationSearch
  }, [params.statuses, params.locationSearch])

  const isPlanBenefits = dataType === 'PLAN_BENEFITS'
  const isPlanAndRates = dataType === 'PLANS_AND_RATES'
  const supportsDeletedTab = isPlanBenefits || isPlanAndRates

  const [activeTab, setActiveTab] = useState<'report' | 'deleted'>('report')
  const activeTabRef = useRef<'report' | 'deleted'>('report')
  activeTabRef.current = activeTab

  const [deletedTotal, setDeletedTotal] = useState(0)
  const [deletedError, setDeletedError] = useState<string | null>(null)
  const deletedGridRef = useRef<GridReadyEvent | undefined>(undefined)
  const deletedRequestVersionRef = useRef(0)
  const deletedTotalRef = useRef(0)

  const [_hasDraftRows, setHasDraftRows] = useState(false)

  const [draftOnly, setDraftOnly] = useState(false)
  const draftOnlyRef = useRef(false)
  draftOnlyRef.current = draftOnly

  const [reviewCell, setReviewCell] = useState<ReviewCellInfo | null>(null)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [popoverCell, setPopoverCell] = useState<PopoverCellInfo | null>(null)
  const [parReviewCell, setParReviewCell] =
    useState<PlanAndRateReviewCellInfo | null>(null)
  const [parPopoverCell, setParPopoverCell] =
    useState<PlanAndRatePopoverCellInfo | null>(null)

  // Stable indirection so the action column def stays referentially stable
  // while still calling the latest handler.
  const onApproveRef = useRef<(row: Record<string, unknown>) => Promise<void>>(
    () => Promise.resolve()
  )
  const onViewRef = useRef<(row: Record<string, unknown>) => Promise<void>>(
    () => Promise.resolve()
  )
  const pendingCountByRow = useRef<Map<string, number>>(new Map())
  const pendingWaitersRef = useRef<Map<string, Array<() => void>>>(new Map())

  const registerPending = useCallback((rowId: string) => {
    pendingCountByRow.current.set(
      rowId,
      (pendingCountByRow.current.get(rowId) ?? 0) + 1
    )
  }, [])

  const unregisterPending = useCallback((rowId: string) => {
    const next = (pendingCountByRow.current.get(rowId) ?? 1) - 1
    if (next <= 0) {
      pendingCountByRow.current.delete(rowId)
      const cbs = pendingWaitersRef.current.get(rowId) ?? []
      pendingWaitersRef.current.delete(rowId)
      cbs.forEach((cb) => cb())
    } else {
      pendingCountByRow.current.set(rowId, next)
    }
  }, [])

  const waitForRowSettled = useCallback((rowId: string): Promise<void> => {
    if ((pendingCountByRow.current.get(rowId) ?? 0) === 0)
      return Promise.resolve()
    return new Promise((resolve) => {
      const existing = pendingWaitersRef.current.get(rowId) ?? []
      pendingWaitersRef.current.set(rowId, [...existing, resolve])
    })
  }, [])

  const columnDefs = useMemo(() => {
    const base: ColDef[] = [
      STAGE_BADGE_COL_DEF,
      ...getCompareReportColDefs(dataType, {
        onCellClickable: isPlanBenefits || isPlanAndRates,
      }),
    ]
    if (!isPlanBenefits && !isPlanAndRates) return base
    const actionCol: ColDef = {
      colId: '__approveAction',
      headerName: '',
      pinned: 'right',
      width: 130,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      cellRenderer: RowActionCell,
      cellRendererParams: {
        onView: (row: Record<string, unknown>) => onViewRef.current(row),
        onApprove: (row: Record<string, unknown>) => onApproveRef.current(row),
      },
    }
    return [...base, actionCol]
  }, [dataType, isPlanBenefits, isPlanAndRates])

  const onIgnoreRef = useRef<(row: Record<string, unknown>) => Promise<void>>(
    () => Promise.resolve()
  )

  const deletedColumnDefs = useMemo(() => {
    const base: ColDef[] = [
      { ...STAGE_BADGE_COL_DEF, valueGetter: () => 'DELETED' },
      ...getCompareReportColDefs(dataType, { sortable: false }),
    ]
    const ignoreCol: ColDef = {
      colId: '__ignoreAction',
      headerName: '',
      pinned: 'right',
      width: 80,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      cellRenderer: IgnoreActionCell,
      cellRendererParams: {
        onIgnore: (row: Record<string, unknown>) => onIgnoreRef.current(row),
      },
    }
    return [...base, ignoreCol]
  }, [dataType])

  const handleCellClicked = useCallback(
    (event: CellClickedEvent) => {
      const field = event.colDef.field
      if (!field) return
      const rowData = event.data as Record<string, unknown>
      if (rowData.__overrideStatus === 'APPROVED') return
      const target = event.event?.target as HTMLElement | null
      if (!target) return

      if (isPlanBenefits) {
        if (PLAN_BENEFITS_IDENTITY.has(field)) return
        setPopoverCell({ field, rowData, anchor: target })
        return
      }

      if (isPlanAndRates) {
        if (PLANS_AND_RATES_NON_EDITABLE_COLS.has(field)) return
        setParPopoverCell({ field, rowData, anchor: target })
      }
    },
    [isPlanBenefits, isPlanAndRates]
  )

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridRef.current = params
    requestVersionRef.current += 1
    totalRef.current = 0
    setTotal(0)
    setError(null)

    const getRows = async (rowParams: IServerSideGetRowsParams) => {
      const version = requestVersionRef.current

      try {
        const blockSize =
          rowParams.request.endRow != null
            ? rowParams.request.endRow - (rowParams.request.startRow ?? 0)
            : DEFAULT_PAGE_SIZE
        const page =
          Math.floor((rowParams.request.startRow ?? 0) / blockSize) + 1

        const sortItem = rowParams.request.sortModel?.[0]

        const sharedParams = {
          taskId: taskIdRef.current,
          dataType: dataTypeRef.current as NewCompareDataType,
          states: globalStatesRef.current.length
            ? globalStatesRef.current
            : undefined,
          locationSearch:
            dataTypeRef.current === 'LOCATION'
              ? locationSearchRef.current || undefined
              : undefined,
          issuerId: globalIssuerIdRef.current || undefined,
          hiosPlanId: globalHiosPlanIdRef.current || undefined,
          sort: sortItem?.colId,
          order: sortItem?.sort as 'asc' | 'desc' | undefined,
          page,
          limit: blockSize,
        }

        const result = await getCompare({
          ...sharedParams,
          statuses:
            selectedStatusesRef.current.length < ALL_STATUSES.length
              ? selectedStatusesRef.current
              : undefined,
          overrideStatus: draftOnlyRef.current ? 'DRAFT' : undefined,
        })

        if (params.api.isDestroyed() || version !== requestVersionRef.current) {
          if (!params.api.isDestroyed()) rowParams.fail()
          return
        }

        const flatRows = result.items.map((row) => {
          const stagingFlat = row.stagingData
            ? normalizeMigrateKeys(row.stagingData)
            : {}
          const liveFlat = row.liveData
            ? normalizeMigrateKeys(row.liveData)
            : undefined
          const overrideFlat = row.overrideParams
            ? normalizeMigrateKeys(row.overrideParams)
            : null
          return {
            id: row.naturalKeyHash,
            ...(liveFlat ?? {}),
            ...stagingFlat,
            __compareStatus: row.status,
            ...(liveFlat ? { _currentValue: liveFlat } : {}),
            __overrideParams: overrideFlat,
            __paramSources: row.overrideParamSources ?? null,
            __overrideStatus: row.overrideStatus ?? null,
          }
        })

        const rowCount = result.total ?? 0
        rowParams.success({ rowData: flatRows, rowCount })

        if (!params.api.isDestroyed()) {
          if (rowCount === 0) {
            params.api.showNoRowsOverlay()
          } else {
            params.api.hideOverlay()
          }
        }

        if (result.total != null && result.total !== totalRef.current) {
          totalRef.current = result.total
          setTotal(result.total)
        }

        if (!params.api.isDestroyed()) {
          setHasDraftRows(checkDraftRows(params.api))
        }
      } catch {
        if (version !== requestVersionRef.current) {
          if (!params.api.isDestroyed()) rowParams.fail()
          return
        }
        setError('Failed to load data. Please try again.')
        if (!params.api.isDestroyed()) rowParams.fail()
      }
    }

    params.api.updateGridOptions({
      serverSideDatasource: { getRows },
    })

    if (initialPageRef.current > 1) {
      params.api.paginationGoToPage(initialPageRef.current - 1)
    }
  }, [])

  const refreshGrid = useCallback(() => {
    setError(null)
    setTotal(0)
    setHasDraftRows(false)
    totalRef.current = 0
    requestVersionRef.current += 1
    gridRef.current?.api.paginationGoToPage(0)
    gridRef.current?.api.refreshServerSide({ purge: true })
  }, [])

  const deletedOnGridReady = useCallback((params: GridReadyEvent) => {
    deletedGridRef.current = params
    deletedRequestVersionRef.current += 1
    deletedTotalRef.current = 0
    setDeletedTotal(0)
    setDeletedError(null)

    const getRows = async (rowParams: IServerSideGetRowsParams) => {
      const version = deletedRequestVersionRef.current
      try {
        const blockSize =
          rowParams.request.endRow != null
            ? rowParams.request.endRow - (rowParams.request.startRow ?? 0)
            : DEFAULT_PAGE_SIZE
        const page =
          Math.floor((rowParams.request.startRow ?? 0) / blockSize) + 1

        const result = await getDeletedData({
          taskId: taskIdRef.current,
          dataType: dataTypeRef.current as DeletedDataType,
          states: globalStatesRef.current.length
            ? globalStatesRef.current
            : undefined,
          issuerId: globalIssuerIdRef.current || undefined,
          hiosPlanId: globalHiosPlanIdRef.current || undefined,
          page,
          limit: blockSize,
        })

        if (
          params.api.isDestroyed() ||
          version !== deletedRequestVersionRef.current
        ) {
          if (!params.api.isDestroyed()) rowParams.fail()
          return
        }

        const flatRows = result.items.map((row) => {
          const liveFlat = row.liveData
            ? normalizeMigrateKeys(row.liveData)
            : {}
          return {
            id: row.naturalKeyHash,
            ...liveFlat,
            __compareStatus: 'DELETED',
          }
        })

        const rowCount = result.total ?? 0
        rowParams.success({ rowData: flatRows, rowCount })
        if (!params.api.isDestroyed()) {
          if (rowCount === 0) params.api.showNoRowsOverlay()
          else params.api.hideOverlay()
        }
        if (result.total !== deletedTotalRef.current) {
          deletedTotalRef.current = result.total
          setDeletedTotal(result.total)
        }
      } catch {
        if (version !== deletedRequestVersionRef.current) {
          if (!params.api.isDestroyed()) rowParams.fail()
          return
        }
        setDeletedError('Failed to load deleted data. Please try again.')
        if (!params.api.isDestroyed()) rowParams.fail()
      }
    }

    params.api.updateGridOptions({ serverSideDatasource: { getRows } })
  }, [])

  const refreshDeletedGrid = useCallback(() => {
    setDeletedError(null)
    setDeletedTotal(0)
    deletedTotalRef.current = 0
    deletedRequestVersionRef.current += 1
    deletedGridRef.current?.api.paginationGoToPage(0)
    deletedGridRef.current?.api.refreshServerSide({ purge: true })
  }, [])

  const handleReviewSaved = useCallback(
    (rowId: string, update: ReviewSavedUpdate) => {
      setError(null)
      const api = gridRef.current?.api
      if (!api) return
      const node = api.getRowNode(rowId)
      if (!node) return
      node.setData({
        ...node.data,
        __overrideParams: update.params,
        __overrideStatus: update.status,
      })
      api.flashCells({
        rowNodes: [node],
        flashDuration: 600,
        fadeDuration: 400,
      })
      setHasDraftRows(checkDraftRows(api))
    },
    []
  )

  const shouldAutoSize = dataType === 'LOCATION' || dataType === 'SMOKER_RATES'

  const handleFirstDataRendered = useCallback(
    (event: { api: { sizeColumnsToFit: () => void } }) => {
      if (shouldAutoSize) event.api.sizeColumnsToFit()
    },
    [shouldAutoSize]
  )

  const handleModelUpdated = useCallback((event: ModelUpdatedEvent) => {
    const rowId = pendingHighlightRef.current
    if (!rowId) return
    const node = event.api.getRowNode(rowId)
    if (!node) return
    pendingHighlightRef.current = null
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current)
      highlightTimeoutRef.current = null
    }
    // Scroll the row into view, then flash it on the next frame once its cells
    // are rendered.
    event.api.ensureNodeVisible(node, 'middle')
  }, [])

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current)
    }
  }, [])

  const handleApproveRowRef = useRef<
    (row: Record<string, unknown>) => Promise<void>
  >(() => Promise.resolve())
  handleApproveRowRef.current = async (row: Record<string, unknown>) => {
    const match = {
      year: row.year as number,
      hiosPlanId: row.hiosPlanId as string,
      benefitName: row.benefit as string,
    }
    const existingOverrideParams =
      (row.__overrideParams as Record<string, unknown>) ?? {}
    const params: Record<string, unknown> = {}
    const paramSources: Record<string, PlanBenefitOverrideValueSource> = {}

    for (const field of Object.keys(FIELD_CONFIG)) {
      if (IDENTITY_FIELDS.has(field) || !(field in row)) continue
      if (existingOverrideParams[field] !== undefined) {
        params[field] = coerceParamValue(field, existingOverrideParams[field])
        paramSources[field] =
          (
            row.__paramSources as
              | Record<string, PlanBenefitOverrideValueSource>
              | undefined
          )?.[field] ?? PlanBenefitOverrideValueSource.OVERRIDE_DATA
      } else {
        params[field] = coerceParamValue(field, row[field])
        paramSources[field] = PlanBenefitOverrideValueSource.IDEON_DATA
      }
    }

    try {
      const existing = await listOverrideRules({ ...match, limit: 1 })
      const rule = existing.data[0] ?? null
      let ruleId: string

      if (!rule) {
        const created = await createOverrideRule({
          template: PlanBenefitOverrideRuleTemplateKey.PARTIAL_FIELD_UPDATE,
          match,
          params,
          paramSources,
        })
        ruleId = created.id
      } else {
        await updateOverrideRule(rule.id, {
          params: { ...(rule.params ?? {}), ...params },
          paramSources: { ...(rule.paramSources ?? {}), ...paramSources },
        })
        ruleId = rule.id
      }

      if (rule?.status !== PlanBenefitOverrideRuleStatus.APPROVED) {
        await updateOverrideRuleStatus(ruleId, {
          status: PlanBenefitOverrideRuleStatus.APPROVED,
        })
      }

      const api = gridRef.current?.api
      if (api && !api.isDestroyed()) {
        const rowId = row.id as string
        const node = api.getRowNode(rowId)
        if (node) {
          node.setData({
            ...row,
            __overrideParams: params,
            __overrideStatus: 'APPROVED',
          })
        }
        setHasDraftRows(checkDraftRows(api))
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [OVERRIDE_RULES_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [FIND_OVERRIDE_RULE_QUERY_KEY],
        }),
      ])
    } catch {
      message.error('Failed to approve override. Please try again.')
      throw new Error('approve failed')
    }
  }

  const handleApproveParRowRef = useRef<
    (row: Record<string, unknown>) => Promise<void>
  >(() => Promise.resolve())
  handleApproveParRowRef.current = async (row: Record<string, unknown>) => {
    const match = {
      year: row.year as number,
      hiosPlanId: row.hiosPlanId as string,
      state: row.state as string,
    }
    const existingOverrideFlat = row.__overrideParams
      ? flattenRuleParams(row.__overrideParams as Record<string, unknown>)
      : {}
    const flatParams: Record<string, unknown> = {}
    const paramSources: Record<string, string> = {}

    for (const field of Object.keys(PAR_FIELD_CONFIG)) {
      if (PAR_IDENTITY_FIELDS.has(field)) continue
      if (existingOverrideFlat[field] !== undefined) {
        flatParams[field] = parCoerceParamValue(
          field,
          existingOverrideFlat[field]
        )
        paramSources[field] =
          (row.__paramSources as Record<string, string> | undefined)?.[field] ??
          'OVERRIDE_DATA'
      } else {
        const stagingVal = getStagingValue(field, row)
        if (PAR_AREA_FACTOR_KEYS.has(field)) {
          // Always include every area factor key — null if no staging data
          flatParams[field] = parCoerceParamValue(field, stagingVal)
          paramSources[field] = 'IDEON_DATA'
        } else if (stagingVal !== null && stagingVal !== undefined) {
          flatParams[field] = parCoerceParamValue(field, stagingVal)
          paramSources[field] = 'IDEON_DATA'
        }
      }
    }

    const nestedParams = nestRuleParams(flatParams)

    try {
      const existing = await findPlanAndRateOverrideRuleByMatch(match)
      let ruleId: string

      if (!existing) {
        const created = await createPlanAndRateOverrideRule({
          template: 'PARTIAL_FIELD_UPDATE',
          match,
          params: nestedParams,
          paramSources: paramSources as Record<
            string,
            PlanAndRateOverrideValueSource
          >,
        })
        ruleId = created.id
      } else {
        await updatePlanAndRateOverrideRule(existing.id, {
          params: { ...(existing.params ?? {}), ...nestedParams },
          paramSources: {
            ...(existing.paramSources ?? {}),
            ...paramSources,
          } as Record<string, PlanAndRateOverrideValueSource>,
        })
        ruleId = existing.id
      }

      if (existing?.status !== PlanAndRateOverrideRuleStatus.APPROVED) {
        await updatePlanAndRateOverrideRuleStatus(ruleId, {
          status: PlanAndRateOverrideRuleStatus.APPROVED,
        })
      }

      const api = gridRef.current?.api
      if (api && !api.isDestroyed()) {
        const rowId = row.id as string
        const node = api.getRowNode(rowId)
        if (node) {
          node.setData({
            ...row,
            __overrideParams: nestedParams,
            __overrideStatus: 'APPROVED',
          })
        }
        setHasDraftRows(checkDraftRows(api))
      }

      await queryClient.invalidateQueries({
        queryKey: [FIND_PLAN_AND_RATE_RULE_QUERY_KEY],
      })
    } catch {
      message.error('Failed to approve override. Please try again.')
      throw new Error('approve failed')
    }
  }

  const handleIgnoreParRowRef = useRef<
    (row: Record<string, unknown>) => Promise<void>
  >(() => Promise.resolve())
  handleIgnoreParRowRef.current = async (row: Record<string, unknown>) => {
    const match = {
      year: row.year as number,
      hiosPlanId: row.hiosPlanId as string,
      state: row.state as string,
    }
    const flatParams: Record<string, unknown> = {}

    for (const field of Object.keys(PAR_FIELD_CONFIG)) {
      if (PAR_IDENTITY_FIELDS.has(field)) continue
      const val = getStagingValue(field, row)
      if (PAR_AREA_FACTOR_KEYS.has(field)) {
        flatParams[field] = parCoerceParamValue(field, val)
      } else if (val !== null && val !== undefined) {
        flatParams[field] = parCoerceParamValue(field, val)
      }
    }

    const nestedParams = nestRuleParams(flatParams)

    try {
      await createPlanAndRateOverrideRule({
        template: 'UPSERT_RULE',
        match,
        params: nestedParams,
        paramSources: Object.fromEntries(
          Object.keys(flatParams).map((k) => [
            k,
            'IDEON_DATA' as PlanAndRateOverrideValueSource,
          ])
        ) as Record<string, PlanAndRateOverrideValueSource>,
      })

      const api = deletedGridRef.current?.api
      if (api && !api.isDestroyed()) {
        const rowId = row.id as string
        const node = api.getRowNode(rowId)
        if (node) api.applyServerSideTransaction({ remove: [node.data] })
      }
    } catch {
      message.error('Failed to ignore row. Please try again.')
      throw new Error('ignore failed')
    }
  }

  const handleIgnoreBenefitRowRef = useRef<
    (row: Record<string, unknown>) => Promise<void>
  >(() => Promise.resolve())
  handleIgnoreBenefitRowRef.current = async (row: Record<string, unknown>) => {
    const match = {
      year: row.year as number,
      hiosPlanId: row.hiosPlanId as string,
      benefitName: row.benefit as string,
    }
    const params: Record<string, unknown> = {}
    const paramSources: Record<string, PlanBenefitOverrideValueSource> = {}

    for (const field of Object.keys(FIELD_CONFIG)) {
      if (IDENTITY_FIELDS.has(field)) continue
      const val = row[field]
      if (val !== null && val !== undefined) {
        params[field] = coerceParamValue(field, val)
        paramSources[field] = PlanBenefitOverrideValueSource.IDEON_DATA
      }
    }

    try {
      await createOverrideRule({
        template: PlanBenefitOverrideRuleTemplateKey.UPSERT_RULE,
        match,
        params,
        paramSources,
      })

      const api = deletedGridRef.current?.api
      if (api && !api.isDestroyed()) {
        const rowId = row.id as string
        const node = api.getRowNode(rowId)
        if (node) api.applyServerSideTransaction({ remove: [node.data] })
      }
    } catch {
      message.error('Failed to ignore row. Please try again.')
      throw new Error('ignore failed')
    }
  }

  if (isPlanAndRates) {
    onIgnoreRef.current = (row) => handleIgnoreParRowRef.current(row)
  } else {
    onIgnoreRef.current = (row) => handleIgnoreBenefitRowRef.current(row)
  }

  if (isPlanAndRates) {
    onApproveRef.current = (row) => handleApproveParRowRef.current(row)
    onViewRef.current = async (row) => {
      const rowId = row.id as string
      await waitForRowSettled(rowId)
      setParReviewCell({ rowData: row })
    }
  } else {
    onApproveRef.current = (row) => handleApproveRowRef.current(row)
    onViewRef.current = async (row) => {
      const rowId = row.id as string
      await waitForRowSettled(rowId)
      setReviewCell({ field: '__all__', rowData: row })
    }
  }

  const handlePopoverOptimisticUpdate = useCallback(
    (
      rowId: string,
      overrideParams: Record<string, unknown>,
      overrideStatus: string
    ) => {
      registerPending(rowId)
      const api = gridRef.current?.api
      if (!api || api.isDestroyed()) return
      const node = api.getRowNode(rowId)
      if (node)
        node.setData({
          ...node.data,
          __overrideParams: overrideParams,
          __overrideStatus: overrideStatus,
        })
    },
    [registerPending]
  )

  const handlePopoverRevert = useCallback(
    (rowId: string, originalData: Record<string, unknown>) => {
      const api = gridRef.current?.api
      if (!api || api.isDestroyed()) return
      const node = api.getRowNode(rowId)
      if (node) node.setData(originalData)
      unregisterPending(rowId)
      setHasDraftRows(checkDraftRows(api))
    },
    [unregisterPending]
  )

  const handlePopoverCommit = useCallback(
    (
      rowId: string,
      overrideParams: Record<string, unknown>,
      overrideStatus: string
    ) => {
      const api = gridRef.current?.api
      if (!api || api.isDestroyed()) return
      const node = api.getRowNode(rowId)
      if (node)
        node.setData({
          ...node.data,
          __overrideParams: overrideParams,
          __overrideStatus: overrideStatus,
        })
      unregisterPending(rowId)
      setHasDraftRows(checkDraftRows(api))
    },
    [unregisterPending]
  )

  useEffect(() => {
    refreshGrid()
    if (activeTabRef.current === 'deleted') refreshDeletedGrid()
  }, [
    globalState,
    globalIssuerId,
    globalHiosPlanId,
    refreshGrid,
    refreshDeletedGrid,
  ])

  const handleTabChange = useCallback((key: string) => {
    const next = key as 'report' | 'deleted'
    setActiveTab(next)
    activeTabRef.current = next
    if (next === 'deleted') {
      setDraftOnly(false)
      draftOnlyRef.current = false
    }
  }, [])

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      gridRef.current?.api.updateGridOptions({ cacheBlockSize: newSize })
      setParams(
        { pageSize: String(newSize), page: undefined },
        { replace: true }
      )
      refreshGrid()
    },
    [refreshGrid, setParams]
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setParams(
        { page: page > 1 ? String(page) : undefined },
        { replace: true }
      )
    },
    [setParams]
  )

  const handleSortChanged = useCallback(() => {
    // AG Grid re-requests rows with the new sort model automatically; reset to
    // the first page so the user sees the start of the newly sorted list.
    setParams({ page: undefined }, { replace: true })
    gridRef.current?.api.paginationGoToPage(0)
  }, [setParams])

  const handleStatusesChange = useCallback(
    (value: CompareStatus[]) => {
      const next = value.length ? value : ALL_STATUSES
      selectedStatusesRef.current = next
      setSelectedStatuses(next)
      setParams(
        {
          statuses:
            next.length < ALL_STATUSES.length ? next.join(',') : undefined,
          page: undefined,
        },
        { replace: true }
      )
      refreshGrid()
    },
    [refreshGrid, setParams]
  )

  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownloadCsv = useCallback(async () => {
    const url = getDataTypeCsvExportUrl(
      taskId,
      dataType as NewCompareDataType,
      {
        statuses:
          selectedStatusesRef.current.length < ALL_STATUSES.length
            ? selectedStatusesRef.current
            : undefined,
        states: globalStatesRef.current.length
          ? globalStatesRef.current
          : undefined,
        locationSearch:
          dataType === 'LOCATION'
            ? locationSearchRef.current || undefined
            : undefined,
        issuerId: globalIssuerIdRef.current || undefined,
        hiosPlanId: globalHiosPlanIdRef.current || undefined,
      }
    )

    setIsDownloading(true)
    const filename = `${dataType.toLowerCase()}_${dayjs().unix()}.csv`
    try {
      if ('showSaveFilePicker' in window) {
        const handle = await (
          window as typeof window & {
            showSaveFilePicker: (opts: unknown) => Promise<{
              createWritable: () => Promise<WritableStream>
            }>
          }
        ).showSaveFilePicker({
          suggestedName: filename,
          types: [{ description: 'CSV', accept: { 'text/csv': ['.csv'] } }],
        })
        const writable = await handle.createWritable()
        const response = await fetch(url, { headers: getAuthHeader() })
        if (!response.ok || !response.body) throw new Error('Download failed')
        await response.body.pipeTo(writable)
      } else {
        const response = await fetch(url, { headers: getAuthHeader() })
        if (!response.ok) throw new Error('Download failed')
        const blob = await response.blob()
        const anchor = document.createElement('a')
        anchor.href = URL.createObjectURL(blob)
        anchor.download = filename
        anchor.click()
        setTimeout(() => URL.revokeObjectURL(anchor.href), 100)
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        // user cancelled the save dialog — not an error
      } else {
        message.error('CSV download failed. Please try again.')
      }
    } finally {
      setIsDownloading(false)
    }
  }, [taskId, dataType])

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    }
  }, [])

  const handleSearchChange = useCallback(
    (
      ref: React.MutableRefObject<string>,
      setter: React.Dispatch<React.SetStateAction<string>>,
      paramKey: string,
      value: string
    ) => {
      ref.current = value
      setter(value)
      setParams(
        { [paramKey]: value || undefined, page: undefined },
        { replace: true }
      )
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
      searchDebounceRef.current = setTimeout(() => {
        refreshGrid()
      }, 400)
    },
    [refreshGrid, setParams]
  )

  if (!enabled) {
    return (
      <>
        <div className={styles.headerRow}>
          <PlanSnapshotInfo dataType={dataType} total={0} />
        </div>
        <div className={styles.maintenanceWrapper}>
          <Card className={styles.maintenanceCard}>
            <div className={styles.maintenanceContent}>
              <ToolOutlined className={styles.maintenanceIcon} />
              <Typography.Title level={4} style={{ margin: '16px 0 8px' }}>
                Under Maintenance
              </Typography.Title>
              <Typography.Text
                type="secondary"
                className={styles.maintenanceDesc}
              >
                Data preview for this type is currently being set up.
              </Typography.Text>
            </div>
          </Card>
        </div>
      </>
    )
  }

  const tabToggles = supportsDeletedTab && (
    <>
      {(['report', 'deleted'] as const).map((tab) => {
        const isActive = activeTab === tab
        return (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            style={{
              padding: '0 4px 8px',
              borderRadius: 0,
              fontSize: 14,
              fontWeight: isActive ? 600 : 400,
              cursor: 'pointer',
              border: 'none',
              borderBottom: `2px solid ${isActive ? '#1677ff' : 'transparent'}`,
              backgroundColor: 'transparent',
              color: isActive ? '#1677ff' : '#6b7280',
              transition: 'all 0.15s',
              lineHeight: 1.5,
              marginTop: 5,
            }}
          >
            {tab === 'report' ? 'All Records' : 'Deleted Records'}
          </button>
        )
      })}
    </>
  )

  return (
    <>
      <div className={styles.headerRow}>
        <PlanSnapshotInfo
          dataType={dataType}
          total={activeTab === 'deleted' ? deletedTotal : total}
        />
      </div>
      <div className={styles.filterRow}>
        {tabToggles}
        {dataType === 'LOCATION' && activeTab === 'report' && (
          <Input
            allowClear
            placeholder="Search by ZIP or FIPS code"
            value={locationSearch}
            onChange={(e) =>
              handleSearchChange(
                locationSearchRef,
                setLocationSearch,
                'locationSearch',
                e.target.value
              )
            }
            style={{ width: 240 }}
          />
        )}
      </div>
      <div className={styles.stageRow}>
        {activeTab === 'deleted' ? (
          <button
            disabled
            style={{
              padding: '4px 14px',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'default',
              border: `1.5px solid ${STAGE_STYLES['DELETED'].bg}`,
              backgroundColor: STAGE_STYLES['DELETED'].bg,
              color: STAGE_STYLES['DELETED'].color,
              lineHeight: 1.5,
            }}
          >
            Deleted
          </button>
        ) : (
          STAGE_OPTIONS.map(({ label, value }) => {
            const active = selectedStatuses.includes(value as CompareStatus)
            const s = STAGE_STYLES[value] ?? { bg: '#f3f4f6', color: '#6b7280' }
            return (
              <button
                key={value}
                onClick={() => {
                  const next = active
                    ? selectedStatuses.filter((s) => s !== value)
                    : [...selectedStatuses, value as CompareStatus]
                  handleStatusesChange(next as CompareStatus[])
                }}
                style={{
                  padding: '4px 14px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1.5px solid ${active ? s.bg : '#e5e7eb'}`,
                  backgroundColor: active ? s.bg : 'transparent',
                  color: active ? s.color : '#9ca3af',
                  transition: 'all 0.15s',
                  lineHeight: 1.5,
                }}
              >
                {label}
              </button>
            )
          })
        )}
        {isPlanBenefits && activeTab === 'report' && (
          <label
            style={{
              marginLeft: 16,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              color: '#374151',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={draftOnly}
              onChange={(e) => {
                setDraftOnly(e.target.checked)
                draftOnlyRef.current = e.target.checked
                refreshGrid()
              }}
              style={{ display: 'none' }}
            />
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: 3,
                backgroundColor: draftOnly ? '#3b82f6' : '#fff',
                border: `1.5px solid ${draftOnly ? '#3b82f6' : '#d1d5db'}`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {draftOnly && (
                <CheckOutlined style={{ fontSize: 10, color: '#fff' }} />
              )}
            </span>
            Records need to review
          </label>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {isPlanBenefits && (
            <Button
              icon={<UploadOutlined />}
              onClick={() => setIsImportOpen(true)}
              style={{
                visibility: activeTab === 'report' ? 'visible' : 'hidden',
              }}
            >
              Import New Benefits
            </Button>
          )}
          <Button
            icon={<DownloadOutlined />}
            loading={isDownloading}
            onClick={handleDownloadCsv}
            style={{
              visibility: activeTab === 'deleted' ? 'hidden' : 'visible',
            }}
          >
            Download CSV
          </Button>
        </div>
      </div>
      {activeTab === 'report' && error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {activeTab === 'deleted' && deletedError && (
        <Alert
          type="error"
          message={deletedError}
          showIcon
          closable
          onClose={() => setDeletedError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {activeTab === 'report' && (
        <PlanReviewGrid
          columnDefs={columnDefs}
          onGridReady={onGridReady}
          onSortChanged={handleSortChanged}
          onPageSizeChange={handlePageSizeChange}
          pageSize={parsedInitialPageSize}
          onPageChange={handlePageChange}
          onCellClicked={
            isPlanBenefits || isPlanAndRates ? handleCellClicked : undefined
          }
          onModelUpdated={isPlanBenefits ? handleModelUpdated : undefined}
          onFirstDataRendered={
            shouldAutoSize ? handleFirstDataRendered : undefined
          }
          getRowId={(p) => p.data.id}
        />
      )}
      {activeTab === 'deleted' && (
        <PlanReviewGrid
          columnDefs={deletedColumnDefs}
          onGridReady={deletedOnGridReady}
          onPageSizeChange={() => {}}
          pageSize={DEFAULT_PAGE_SIZE}
          getRowId={(p) => p.data.id}
        />
      )}
      {isPlanBenefits && (
        <>
          <PlanBenefitReviewModal
            open={reviewCell !== null}
            cellInfo={reviewCell}
            onClose={() => setReviewCell(null)}
            onSaved={handleReviewSaved}
          />
          <ImportNewBenefitsModal
            taskId={taskId}
            open={isImportOpen}
            onClose={() => setIsImportOpen(false)}
            onDone={refreshGrid}
          />
          <PlanBenefitCellPopover
            cell={popoverCell}
            onClose={() => setPopoverCell(null)}
            onOptimisticUpdate={handlePopoverOptimisticUpdate}
            onRevert={handlePopoverRevert}
            onCommit={handlePopoverCommit}
          />
        </>
      )}
      {isPlanAndRates && (
        <>
          <PlanAndRateReviewModal
            open={parReviewCell !== null}
            cellInfo={parReviewCell}
            onClose={() => setParReviewCell(null)}
            onSaved={(rowId, update) => {
              setError(null)
              const api = gridRef.current?.api
              if (!api) return
              const node = api.getRowNode(rowId)
              if (!node) return
              node.setData({
                ...node.data,
                __overrideParams: update.params,
                __overrideStatus: update.status,
              })
              api.flashCells({
                rowNodes: [node],
                flashDuration: 600,
                fadeDuration: 400,
              })
            }}
          />
          <PlanAndRateCellPopover
            cell={parPopoverCell}
            onClose={() => setParPopoverCell(null)}
            onOptimisticUpdate={handlePopoverOptimisticUpdate}
            onRevert={handlePopoverRevert}
            onCommit={handlePopoverCommit}
          />
        </>
      )}
    </>
  )
}
